import { baseLocale, locales, type Locale } from '$lib/paraglide/runtime';
import {
	WIKI_CATEGORIES,
	wikiFrontmatterSchema,
	type WikiCategory,
	type WikiPage
} from './frontmatter';

/**
 * Czysta logika indeksu wiki — zero globów Vite i Svelte, żeby dała się
 * testować w projekcie `logic` (Node). Wejście dostarcza `content.ts`.
 */

export interface WikiIndex {
	/** pageId → wersje językowe strony. */
	byPageId: Map<string, Partial<Record<Locale, WikiPage>>>;
	/** locale → natywny slug → strona. */
	bySlug: Map<Locale, Map<string, WikiPage>>;
}

export interface WikiContentRecord {
	/** Klucz z import.meta.glob, np. `/src/content/wiki/en/concepts/tracker.md`. */
	path: string;
	metadata: unknown;
}

export interface WikiSidebarItem {
	pageId: string;
	title: string;
	href: string;
	order: number;
	/** True gdy bieżący język nie ma tłumaczenia — link prowadzi do treści EN. */
	fallback: boolean;
}

export interface WikiSidebarSection {
	category: WikiCategory;
	items: WikiSidebarItem[];
}

const CONTENT_PATH_PATTERN = /^\/src\/content\/wiki\/([^/]+)\/(.+)\.(?:md|svx)$/;

/** Ścieżka URL strony wiki: EN bez prefiksu, pozostałe języki z `/{locale}`. */
export function wikiHref(locale: Locale, slug?: string): string {
	const prefix = locale === baseLocale ? '' : `/${locale}`;
	return slug === undefined ? `${prefix}/wiki` : `${prefix}/wiki/${slug}`;
}

/**
 * Buduje indeks z rekordów globa. Rzuca (=> fail builda) przy naruszeniu
 * inwariantów: zły frontmatter, slug/lang niezgodny z położeniem pliku,
 * duplikaty, strona bez wersji w języku źródłowym (EN).
 */
export function buildWikiIndex(records: WikiContentRecord[]): WikiIndex {
	const byPageId: WikiIndex['byPageId'] = new Map();
	const bySlug: WikiIndex['bySlug'] = new Map(
		(locales as readonly Locale[]).map((locale) => [locale, new Map<string, WikiPage>()])
	);

	for (const record of records) {
		const match = CONTENT_PATH_PATTERN.exec(record.path);
		if (!match) {
			throw new Error(`Wiki content outside a locale directory: ${record.path}`);
		}
		const [, pathLang, pathSlug] = match;

		const parsed = wikiFrontmatterSchema.safeParse(record.metadata);
		if (!parsed.success) {
			throw new Error(`Invalid wiki frontmatter in ${record.path}: ${parsed.error.message}`);
		}
		const page: WikiPage = { ...parsed.data, filePath: record.path };

		if (page.lang !== pathLang) {
			throw new Error(
				`Wiki frontmatter lang "${page.lang}" does not match directory "${pathLang}": ${record.path}`
			);
		}
		if (page.slug !== pathSlug) {
			throw new Error(
				`Wiki frontmatter slug "${page.slug}" does not match file path "${pathSlug}": ${record.path}`
			);
		}

		const slugMap = bySlug.get(page.lang);
		if (slugMap === undefined) {
			throw new Error(`Unsupported wiki locale directory "${pathLang}": ${record.path}`);
		}
		if (slugMap.has(page.slug)) {
			throw new Error(
				`Duplicate wiki slug "${page.slug}" for locale "${page.lang}": ${record.path}`
			);
		}
		slugMap.set(page.slug, page);

		const translations = byPageId.get(page.pageId) ?? {};
		if (translations[page.lang] !== undefined) {
			throw new Error(
				`Duplicate wiki pageId "${page.pageId}" for locale "${page.lang}": ${record.path}`
			);
		}
		translations[page.lang] = page;
		byPageId.set(page.pageId, translations);
	}

	for (const [pageId, translations] of byPageId) {
		if (translations[baseLocale] === undefined) {
			throw new Error(`Wiki pageId "${pageId}" has no ${baseLocale} (source-of-truth) version`);
		}
	}

	return { byPageId, bySlug };
}

/**
 * Rozwiązuje slug z URL-a na stronę. Brakujące tłumaczenie serwujemy spod
 * sluga EN z `fallback: true` — URL zostaje w języku użytkownika (bez
 * redirectu). Slug EN dla języka, który MA natywne tłumaczenie, nie jest
 * kanoniczny → undefined (404).
 */
export function resolveWikiSlug(
	index: WikiIndex,
	locale: Locale,
	slug: string
): { page: WikiPage; fallback: boolean } | undefined {
	const exact = index.bySlug.get(locale)?.get(slug);
	if (exact !== undefined) return { page: exact, fallback: false };

	if (locale === baseLocale) return undefined;
	const source = index.bySlug.get(baseLocale)?.get(slug);
	if (source === undefined) return undefined;
	if (index.byPageId.get(source.pageId)?.[locale] !== undefined) return undefined;
	return { page: source, fallback: true };
}

/**
 * Drzewo sidebara dla języka. Dla języków innych niż EN zwraca unię
 * wszystkich stron (natywna wersja albo wpis fallback do treści EN) — każdy
 * URL fallbackowy jest podlinkowany, więc crawler prerenderera go odwiedzi.
 */
export function buildSidebar(index: WikiIndex, locale: Locale): WikiSidebarSection[] {
	const sections: WikiSidebarSection[] = [];
	for (const category of WIKI_CATEGORIES) {
		const items: WikiSidebarItem[] = [];
		for (const [pageId, translations] of index.byPageId) {
			const page = translations[locale] ?? translations[baseLocale];
			if (page === undefined || page.category !== category) continue;
			const fallback = locale !== baseLocale && translations[locale] === undefined;
			items.push({
				pageId,
				title: page.title,
				href: wikiHref(locale, page.slug),
				order: page.order,
				fallback
			});
		}
		items.sort((a, b) => a.order - b.order || a.title.localeCompare(b.title));
		if (items.length > 0) sections.push({ category, items });
	}
	return sections;
}

/**
 * Href strony per język — napędza switcher języka (i hreflang w PR2).
 * Brak tłumaczenia → URL fallbackowy `/{locale}/wiki/{enSlug}`.
 */
export function translationHrefs(index: WikiIndex, pageId: string): Record<Locale, string> {
	const translations = index.byPageId.get(pageId);
	const hrefs = {} as Record<Locale, string>;
	for (const locale of locales as readonly Locale[]) {
		const page = translations?.[locale] ?? translations?.[baseLocale];
		if (page !== undefined) hrefs[locale] = wikiHref(locale, page.slug);
	}
	return hrefs;
}

/**
 * Hrefy WYŁĄCZNIE realnych tłumaczeń (bez URL-i fallbackowych) — dla
 * canonical/hreflang/sitemap. Hreflang może łączyć tylko strony kanoniczne;
 * fallbackowe URL-e mają canonical → oryginał EN i nie wchodzą do klastra.
 */
export function realTranslationHrefs(
	index: WikiIndex,
	pageId: string
): Partial<Record<Locale, string>> {
	const translations = index.byPageId.get(pageId);
	const hrefs: Partial<Record<Locale, string>> = {};
	for (const locale of locales as readonly Locale[]) {
		const page = translations?.[locale];
		if (page !== undefined) hrefs[locale] = wikiHref(locale, page.slug);
	}
	return hrefs;
}

/** Slugi EN dla `entries()` — strony PL odkrywa crawler przez linki. */
export function listEntrySlugs(index: WikiIndex): string[] {
	return [...(index.bySlug.get(baseLocale)?.keys() ?? [])];
}
